/**
 * Quick tasks (departments.md "desks"; CFO plan §22): the recurring work of a department, one tap to start.
 * A department doc carries:
 *   homeProjectId  its standing project — where its own recurring work lands (planning → Overhead, receipts → Books)
 *   quickTasks     [{ id, title, scope: 'job' | 'home', bucket?: 'setup' | 'cleanup', tags?, estimatedMinutes? }]
 *                  job  = on the job you're on (setup, cleanup, a material run, travel)
 *                  home = on the department's home project
 * A department with no list of its own and named Field gets FIELD_QUICK_TASKS. Starting one makes (or reuses) today's
 * instance — a task with `quickTaskId` — in the right project and puts the clock on it (commands/quicktasks.js).
 * Pure — no I/O.
 */

export const FIELD_QUICK_TASKS = Object.freeze([
  { id: 'setup', title: 'Setup', scope: 'job', bucket: 'setup' },
  { id: 'cleanup', title: 'Cleanup', scope: 'job', bucket: 'cleanup' },
  { id: 'material-run', title: 'Material run', scope: 'job', tags: ['materials'] },
  { id: 'travel', title: 'Travel', scope: 'job', tags: ['travel'] },
]);

const isField = (d) => d && (d.id === 'field' || String(d.name || '').trim().toLowerCase() === 'field');

/** A department's quick tasks: its own list, else Field's defaults (for Field), else none. */
export function quickTasksFor(dept) {
  if (!dept) return [];
  if (Array.isArray(dept.quickTasks)) return dept.quickTasks.filter((q) => q && q.id && String(q.title || '').trim());
  return isField(dept) ? FIELD_QUICK_TASKS.map((q) => ({ ...q })) : [];
}

/** A clean quick-task list for saving (ids kept, made when missing; empty titles dropped). */
export function cleanQuickTasks(list = [], newId = () => Math.random().toString(36).slice(2, 10)) {
  const seen = new Set();
  return (list || []).map((q) => {
    const title = String(q?.title || '').trim();
    if (!title) return null;
    let id = String(q.id || '').trim() || newId();
    while (seen.has(id)) id = `${id}-${newId()}`;
    seen.add(id);
    const out = { id, title, scope: q.scope === 'home' ? 'home' : 'job' };
    if (q.bucket === 'setup' || q.bucket === 'cleanup') out.bucket = q.bucket;
    if (Array.isArray(q.tags) && q.tags.length) out.tags = q.tags.map((t) => String(t).replace(/^#/, '').trim()).filter(Boolean);
    if (Number(q.estimatedMinutes) > 0) out.estimatedMinutes = Math.round(Number(q.estimatedMinutes));
    return out;
  }).filter(Boolean);
}

/**
 * The department's quick tasks ordered by how you work: how often each was started at this time of day (±1 h) and
 * on this weekday over the last 60 days, then overall; ties keep the department's order.
 */
export function rankQuickTasks(list = [], tasks = [], now = Date.now()) {
  const since = now - 60 * 86400000;
  const hour = new Date(now).getHours();
  const dow = new Date(now).getDay();
  const score = new Map();
  for (const t of tasks || []) {
    if (!t?.quickTaskId) continue;
    const at = Number(t.createdAt) || 0;
    if (at < since) continue;
    const d = new Date(at);
    const s = 1 + (Math.abs(d.getHours() - hour) <= 1 ? 3 : 0) + (d.getDay() === dow ? 2 : 0);
    score.set(t.quickTaskId, (score.get(t.quickTaskId) || 0) + s);
  }
  return list.map((q, i) => ({ q, i, s: score.get(q.id) || 0 })).sort((a, b) => b.s - a.s || a.i - b.i).map((x) => x.q);
}

/** Where a quick task lands: the job you're on (job scope) or the department's home project. */
export function quickTaskProject(q, dept, { currentProjectId = null } = {}) {
  if (!q) return null;
  return q.scope === 'home' ? dept?.homeProjectId || null : currentProjectId || null;
}
